/**
 * Pure timing/geometry helpers for the Hero compositions. No React, no Remotion hooks — everything here is
 * unit-testable and is the single source of truth for `calculateMetadata`, the music envelope and the thread path.
 */
import type { CalculateMetadataFunction } from 'remotion';
import type { CaptionWord } from '../schemas';
import {
  HERO_DIMENSIONS,
  HERO_FPS,
  HeroPropsSchema,
  THREAD_WIPE_SECONDS,
  aspectForCompositionId,
  secondsToFrames,
  validateHeroProps,
  type HeroClip,
  type HeroProps,
  type HeroThread,
} from './schema';

export type HeroSegmentKind = 'open' | 'clip' | 'close';

export interface HeroSegment {
  kind: HeroSegmentKind;
  /** Index into props.clips for kind === 'clip', otherwise -1. */
  clipIndex: number;
  url: string;
  from: number;
  durationInFrames: number;
  trimBefore: number;
  transitionIn: 'cut' | 'thread-wipe';
  matteUrl?: string;
  thread?: HeroThread;
}

export interface HeroTimeline {
  fps: number;
  segments: HeroSegment[];
  wipeFrames: number;
  /** Frames of content (bookends + clips, wipes already overlapped). */
  contentFrames: number;
  logo: { url: string; from: number; durationInFrames: number } | null;
  totalFrames: number;
}

function clipSegment(clip: HeroClip, index: number, fps: number): Omit<HeroSegment, 'from' | 'transitionIn'> {
  return {
    kind: 'clip',
    clipIndex: index,
    url: clip.url,
    durationInFrames: secondsToFrames(clip.durationSec, fps),
    trimBefore: secondsToFrames(clip.trimStartSec ?? 0, fps),
    matteUrl: clip.matteUrl,
    thread: clip.thread,
  };
}

/**
 * Lays the content chain out the way TransitionSeries does: a segment whose transitionIn is a thread wipe starts
 * `wipeFrames` before the previous one ends. The first segment can never wipe (nothing to wipe from).
 */
export function buildHeroTimeline(props: HeroProps, fps: number = HERO_FPS): HeroTimeline {
  const wipeFrames = secondsToFrames(THREAD_WIPE_SECONDS, fps);
  const chain: Array<Omit<HeroSegment, 'from'>> = [];

  if (props.openClip) {
    chain.push({
      kind: 'open',
      clipIndex: -1,
      url: props.openClip.url,
      durationInFrames: secondsToFrames(props.openClip.durationSec, fps),
      trimBefore: 0,
      transitionIn: 'cut',
    });
  }
  props.clips.forEach((clip, i) => {
    chain.push({ ...clipSegment(clip, i, fps), transitionIn: clip.transitionIn });
  });
  if (props.closeClip) {
    chain.push({
      kind: 'close',
      clipIndex: -1,
      url: props.closeClip.url,
      durationInFrames: secondsToFrames(props.closeClip.durationSec, fps),
      trimBefore: 0,
      transitionIn: 'cut',
    });
  }

  const segments: HeroSegment[] = [];
  let cursor = 0;
  chain.forEach((seg, i) => {
    const transitionIn = i === 0 ? 'cut' : seg.transitionIn;
    const from = transitionIn === 'thread-wipe' ? cursor - wipeFrames : cursor;
    segments.push({ ...seg, transitionIn, from });
    cursor = from + seg.durationInFrames;
  });

  const contentFrames = cursor;
  const logo = props.logoCloseClip
    ? { url: props.logoCloseClip.url, from: contentFrames, durationInFrames: secondsToFrames(props.logoCloseClip.durationSec, fps) }
    : null;

  return {
    fps,
    segments,
    wipeFrames,
    contentFrames,
    logo,
    totalFrames: contentFrames + (logo?.durationInFrames ?? 0),
  };
}

/**
 * Remotion calculateMetadata: validates, applies schema defaults, derives the duration. HeroSquare / HeroVertical
 * force their aspect (the component wrapper does the same), so the canvas size and the safe zones can never disagree;
 * a composition with any other id honours `props.aspect`.
 */
export const calculateHeroMetadata: CalculateMetadataFunction<HeroProps> = async ({ props, compositionId }) => {
  const forced = aspectForCompositionId(compositionId);
  const parsed = HeroPropsSchema.parse(forced ? { ...props, aspect: forced } : props);
  const problems = validateHeroProps(parsed, HERO_FPS);
  if (problems.length > 0) {
    throw new Error(`Hero props invalid:\n- ${problems.join('\n- ')}`);
  }
  const timeline = buildHeroTimeline(parsed, HERO_FPS);
  const { width, height } = HERO_DIMENSIONS[parsed.aspect];
  return {
    durationInFrames: Math.max(1, timeline.totalFrames),
    width,
    height,
    props: parsed,
  };
};

// ---------------------------------------------------------------------------
// Audio envelope
// ---------------------------------------------------------------------------

/** Music bed sits at this fraction of musicVolume while narration is playing (0.35 → 0.18 per spec). */
export const MUSIC_DUCKED_RATIO = 0.18 / 0.35;
/** Frames over which the bed slides between levels. */
export const MUSIC_RAMP_FRAMES = 8;

export interface FrameWindow {
  from: number;
  to: number;
}

/** Narration windows in content frames. Without explicit segments, narration spans the whole content. */
export function narrationWindows(props: HeroProps, timeline: HeroTimeline): FrameWindow[] {
  if (!props.narrationUrl) return [];
  if (props.narrationSegments && props.narrationSegments.length > 0) {
    return props.narrationSegments
      .map((s) => ({
        from: secondsToFrames(s.startSec, timeline.fps),
        to: Math.min(timeline.contentFrames, secondsToFrames(s.endSec, timeline.fps)),
      }))
      .filter((w) => w.to > w.from);
  }
  return [{ from: 0, to: timeline.contentFrames }];
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Unramped target level for the bed at a frame. */
function musicTarget(frame: number, props: HeroProps, windows: FrameWindow[], timeline: HeroTimeline): number {
  if (timeline.logo && frame >= timeline.logo.from) return 0;
  const narrating = windows.some((w) => frame >= w.from && frame < w.to);
  return narrating ? props.musicVolume * MUSIC_DUCKED_RATIO : props.musicVolume;
}

/**
 * Music bed volume at a composition frame: musicVolume when free, ducked under narration, silent under the logo
 * close. Level changes are linear slides over MUSIC_RAMP_FRAMES: a ramp DOWN looks ahead and completes exactly when
 * narration / the logo lands, a ramp UP starts at narration end and completes MUSIC_RAMP_FRAMES later — the bed never
 * swells back up under the last words of a line.
 */
export function musicEnvelope(frame: number, props: HeroProps, timeline: HeroTimeline): number {
  if (!props.musicUrl) return 0;
  const windows = narrationWindows(props, timeline);
  // Averaging targets over a look-ahead window anticipates every change; over a look-behind window it lags every
  // change. Taking the lower of the two keeps the anticipating ramp for drops and the lagging ramp for rises.
  let ahead = 0;
  let behind = 0;
  for (let i = 0; i < MUSIC_RAMP_FRAMES; i++) {
    ahead += musicTarget(frame + i, props, windows, timeline);
    behind += musicTarget(frame - i, props, windows, timeline);
  }
  return clamp01(Math.min(ahead, behind) / MUSIC_RAMP_FRAMES);
}

// ---------------------------------------------------------------------------
// Captions
// ---------------------------------------------------------------------------

export interface CaptionPhrase {
  words: CaptionWord[];
  startMs: number;
  endMs: number;
}

export interface GroupCaptionOptions {
  maxWords?: number;
  /** A silence longer than this (ms) starts a new phrase. */
  maxGapMs?: number;
}

/** Phrase-level grouping: break on sentence punctuation, on long gaps, or after maxWords. */
export function groupCaptionPhrases(words: CaptionWord[], opts: GroupCaptionOptions = {}): CaptionPhrase[] {
  const maxWords = opts.maxWords ?? 5;
  const maxGapMs = opts.maxGapMs ?? 700;
  const phrases: CaptionPhrase[] = [];
  let current: CaptionWord[] = [];

  const flush = () => {
    if (current.length === 0) return;
    phrases.push({
      words: current,
      startMs: current[0]!.startMs,
      endMs: current[current.length - 1]!.endMs,
    });
    current = [];
  };

  for (const word of words) {
    const prev = current[current.length - 1];
    if (prev && word.startMs - prev.endMs > maxGapMs) flush();
    current.push(word);
    if (/[.?!]$/.test(word.text.trim()) || current.length >= maxWords) flush();
  }
  flush();
  return phrases;
}

/** A finished phrase lingers this long (ms) before the captions clear, unless the next phrase has already started. */
export const CAPTION_PHRASE_GRACE_MS = 280;

/**
 * The phrase on screen at `currentMs`: the one whose real [startMs, endMs) window contains the time wins, so a phrase
 * that starts the instant the previous one ends is active from its own first word. Only when no phrase is really
 * speaking does the previous phrase linger for the grace window.
 */
export function activeCaptionPhrase<T extends { startMs: number; endMs: number }>(
  phrases: T[],
  currentMs: number,
  graceMs: number = CAPTION_PHRASE_GRACE_MS,
): T | undefined {
  return (
    phrases.find((p) => currentMs >= p.startMs && currentMs < p.endMs) ??
    phrases.find((p) => currentMs >= p.startMs && currentMs < p.endMs + graceMs)
  );
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** CSS `object-fit: cover` placement of a source of size sw×sh inside a dw×dh box. */
export function coverRect(sw: number, sh: number, dw: number, dh: number): Rect {
  const scale = Math.max(dw / sw, dh / sh);
  const w = sw * scale;
  const h = sh * scale;
  return { x: (dw - w) / 2, y: (dh - h) / 2, w, h };
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Centripetal-ish Catmull-Rom through the control points, emitted as cubic Béziers in pixel space.
 * Endpoints are duplicated so the curve starts and ends exactly on the first/last point.
 */
export function threadPath(points: Point[], width: number, height: number): string {
  if (points.length < 2) return '';
  const px = points.map((p) => ({ x: p.x * width, y: p.y * height }));
  const pts = [px[0]!, ...px, px[px.length - 1]!];
  let d = `M ${px[0]!.x.toFixed(2)} ${px[0]!.y.toFixed(2)}`;
  for (let i = 1; i < pts.length - 2; i++) {
    const p0 = pts[i - 1]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2]!;
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    d += ` C ${c1.x.toFixed(2)} ${c1.y.toFixed(2)}, ${c2.x.toFixed(2)} ${c2.y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

/** Fraction of the path the glowing tail trails behind the head spark. */
export const THREAD_TAIL_FRACTION = 0.42;

export interface ThreadState {
  /** Head position along the path, 0–1 (1 = reached the end). */
  head: number;
  /** Tail position along the path, 0–1. */
  tail: number;
  /** 1 while the head is travelling, fading out after it reaches the end. */
  sparkOpacity: number;
  visible: boolean;
}

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/**
 * Where the thread is at `frameInClip`: it enters at enterAt, the head travels the full spline, and the tail has
 * fully left the path by exitAt. Pure function of the frame so the render is deterministic.
 */
export function threadState(frameInClip: number, thread: HeroThread, clipDurationInFrames: number, fps: number): ThreadState {
  const enter = secondsToFrames(thread.enterAt ?? 0, fps);
  const exit = thread.exitAt === undefined ? clipDurationInFrames : secondsToFrames(thread.exitAt, fps);
  const span = Math.max(1, exit - enter);
  const raw = (frameInClip - enter) / span;
  if (raw < 0 || raw > 1) return { head: 0, tail: 0, sparkOpacity: 0, visible: false };
  const p = easeInOutCubic(raw) * (1 + THREAD_TAIL_FRACTION);
  const head = clamp01(p);
  const tail = clamp01(p - THREAD_TAIL_FRACTION);
  const overshoot = Math.max(0, p - 1);
  const sparkOpacity = clamp01(1 - overshoot / 0.12);
  return { head, tail, sparkOpacity, visible: head > tail };
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
